import { ApartmentCompare, ApartmentSearchItem, ApartmentTransactionItem } from '../type/ApartmentCompare.type';
import { useApartmentCompareStore } from '../store/ApartmentCompare.store';

interface ApartmentDataIndexItem {
  id: string;
  name: string;
  dong: string;
  lawdCd: string;
  excluUseAr: number;
  buildYear: number | null;
  households: number;
}

interface ApartmentDataRegionItem {
  id: string;
  name: string;
  area: number;
  series: [number, number, number][]; // [YYYYMM, ㎡당 가격 합계(원), 거래건수]
}

const DATA_BASE_URL = `${process.env.PUBLIC_URL || ''}/data/apartments`;
let apartmentIndexPromise: Promise<ApartmentDataIndexItem[]> | undefined;
const regionDataPromises = new Map<string, Promise<ApartmentDataRegionItem[]>>();
const LAWD_CODE_ALIASES: Record<string, string[]> = {
  // 미추홀구 단지 ID에는 행정구역 명칭 변경 전 남구 코드가 일부 남아 있다.
  '28177': ['28177', '28170'],
};

const fetchJson = async <T,>(url: string): Promise<T> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`정적 아파트 데이터를 불러오지 못했습니다. (${response.status})`);
  return response.json() as Promise<T>;
};

const getApartmentIndex = () => {
  if (!apartmentIndexPromise) {
    apartmentIndexPromise = fetchJson<ApartmentDataIndexItem[]>(`${DATA_BASE_URL}/index.json`).catch((error) => {
      apartmentIndexPromise = undefined;
      throw error;
    });
  }
  return apartmentIndexPromise;
};

const getRegionData = (lawdCd: string) => {
  const cached = regionDataPromises.get(lawdCd);
  if (cached) return cached;

  const request = fetchJson<ApartmentDataRegionItem[]>(`${DATA_BASE_URL}/${lawdCd}.json`).catch((error) => {
    regionDataPromises.delete(lawdCd);
    throw error;
  });
  regionDataPromises.set(lawdCd, request);
  return request;
};

export const useApartmentCompareAPI = () => {
  const {
    insertApartmentCompareToStore,
    getApartmentCompareListFromStore,
    updateApartmentCompareInStore,
    deleteApartmentCompareAtStore,
  } = useApartmentCompareStore();

  const insertApartmentCompare = async (apartmentCompare: ApartmentCompare) =>
    insertApartmentCompareToStore(apartmentCompare);

  const getApartmentCompareList = async () => getApartmentCompareListFromStore();

  const updateApartmentCompare = async (apartmentCompare: ApartmentCompare) =>
    updateApartmentCompareInStore(apartmentCompare);

  const deleteApartmentCompare = async (id: string) => deleteApartmentCompareAtStore(id);

  const fetchApartmentTransactions = async (
    apartmentCompare: ApartmentCompare,
    yearMonthList: string[],
  ): Promise<ApartmentTransactionItem[]> => {
    const dataLawdCd = apartmentCompare.aptSeq?.split('-')[0] || apartmentCompare.lawdCd;
    const regionData = await getRegionData(dataLawdCd);
    const apartment = apartmentCompare.aptSeq
      ? regionData.find((item) => item.id === apartmentCompare.aptSeq)
      : regionData.find(
          (item) => item.name === apartmentCompare.aptName && item.area === Math.round(apartmentCompare.excluUseAr),
        ) || regionData.find((item) => item.name === apartmentCompare.aptName);
    if (!apartment) return [];

    const firstYearMonth = [...yearMonthList].sort()[0];
    const firstRequiredYearMonth = firstYearMonth
      ? `${Number(firstYearMonth.slice(0, 4)) - 1}${firstYearMonth.slice(5, 7)}`
      : undefined;

    return apartment.series
      .map(([yyyymm, pricePerSquareMeterSum, transactionCount]) => {
        const compactYearMonth = String(yyyymm);
        return {
          yearMonth: `${compactYearMonth.slice(0, 4)}-${compactYearMonth.slice(4, 6)}`,
          price: Math.round(((pricePerSquareMeterSum / transactionCount) * apartment.area) / 10_000),
        };
      })
      .filter((item) => !firstRequiredYearMonth || item.yearMonth.replace('-', '') >= firstRequiredYearMonth);
  };

  const searchApartments = async (lawdCd: string, aptNameKeyword: string): Promise<ApartmentSearchItem[]> => {
    const keyword = aptNameKeyword.trim().toLocaleLowerCase('ko-KR');
    const index = await getApartmentIndex();
    const comparableLawdCodes = LAWD_CODE_ALIASES[lawdCd] || [lawdCd];

    return index
      .filter(
        (item) =>
          comparableLawdCodes.includes(item.lawdCd) && item.name.toLocaleLowerCase('ko-KR').includes(keyword),
      )
      .map((item) => ({
        aptSeq: item.id,
        aptName: item.name,
        dongName: item.dong,
        excluUseAr: item.excluUseAr,
        buildYear: item.buildYear,
        households: item.households,
      }))
      .sort((a, b) => a.aptName.localeCompare(b.aptName, 'ko-KR'));
  };

  return {
    insertApartmentCompare,
    getApartmentCompareList,
    updateApartmentCompare,
    deleteApartmentCompare,
    fetchApartmentTransactions,
    searchApartments,
  };
};
